import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import { loginPage } from '../../utils';
import Loading from '../loading/Loading';
import Alert from '../alert/Alert';
import AppLinksOverlay from '../appLinks/AppLinksOverlay';

const getDataFromCookies = () => {
  const sessionId = Cookies.get('_bmeSessionId');
  const resId = Cookies.get('_resId');
  const siteCode = Cookies.get('_siteCode');
  const arrivalDate = Cookies.get('_arrivalDate');
  const lastName = Cookies.get('_lastName');
  const departureDate = Cookies.get('_departureDate');
  const numOfGuests = Cookies.get('_numOfGuests');
  const mainGuestFirstName = Cookies.get('_mainGuestFirstName');
  const numOfAdults = Cookies.get('_numOfAdults');
  const numOfChildren = Cookies.get('_numOfChildren');
  const numOfToddlers = Cookies.get('_numOfToddlers');
  const numOfInfants = Cookies.get('_numOfInfants');
  const numOfUnits = Cookies.get('_numOfUnits');
  const fullName = Cookies.get('_fullName');
  const isDaBooking = Cookies.get('_isDaBooking') === 'true';
  const agencyType = Cookies.get('_agencyType');
  const secretCode = Cookies.get('_secretCode');
  const isEarlyAccess = Cookies.get('_isEarlyAccess') === 'true';
  const seasonType = Cookies.get('_seasonType');
  const isHbs = Cookies.get('_isHbs') === 'true';

  return {
    sessionId,
    resId: parseInt(resId, 10),
    siteCode,
    arrivalDate,
    lastName,
    departureDate,
    mainGuestFirstName,
    numOfGuests: parseInt(numOfGuests, 10),
    numOfAdults: parseInt(numOfAdults, 10),
    numOfChildren: parseInt(numOfChildren, 10),
    numOfToddlers: parseInt(numOfToddlers, 10),
    numOfInfants: parseInt(numOfInfants, 10),
    numOfUnits: parseInt(numOfUnits, 10),
    fullName,
    isDaBooking,
    agencyType,
    secretCode,
    isEarlyAccess,
    seasonType,
    isHbs,
  };
};

const shouldRedirectToLogin = ({
  sessionId,
  resId,
  siteCode,
  arrivalDate,
  lastName,
  departureDate,
  mainGuestFirstName,
  numOfGuests,
  numOfAdults,
  numOfUnits,
  fullName,
}) => !sessionId
|| !resId
|| !siteCode
|| !arrivalDate
|| !lastName
|| !departureDate
|| !mainGuestFirstName
|| !numOfGuests
|| !numOfAdults
|| !numOfUnits
  || !fullName;

const CustomRoute = ({
  children,
  privateRoute = false,
  createSession = () => { },
  setDataFromCookie = () => { },
  bookingErrorMessage = '',
  availableAddonsErrorMessage = '',
  saveAddonErrorMessage = '',
  sessionCreationErrorMessage = '',
  clearBookingSearchErrorMessage = () => { },
  clearAvailableAddonsErrorMessage = () => { },
  clearSaveAddonErrorMessage = () => { },
  clearCreateSessionErrorMessage = () => { },
  clearAvailableEntertainmentsErrorMessage = () => { },
  isFetching,
  nothingToBookErrorMessage,
  availableEntertainmentsErrorMessage,
  notFoundErrorMessage,
  getConfig,
  maxEntertainmentSlotsLessThan7Nights,
  maxEntertainmentSlotsMoreThanOrEqual7Nights,
  bookingPrefix,
  arrivalTimeErrorMessage,
  clearArrivalTimeErrorMessage,
  showAppLinks,
  playStoreLink,
  appStoreLink,
  resortMessage,
  isReloadNecessary,
  setShowAppModal,
}) => {
  const history = useHistory();

  useEffect(() => {
    if (isReloadNecessary) window.location.reload();
  }, [isReloadNecessary]);

  useEffect(() => {
    if (!bookingPrefix) getConfig();
    if (bookingPrefix) createSession();
  }, [createSession, getConfig, bookingPrefix]);

  useEffect(() => {
    if (privateRoute) {
      const dataFromCookies = getDataFromCookies();
      if (shouldRedirectToLogin(dataFromCookies)) {
        history.push(loginPage);
      } else {
        setDataFromCookie({
          ...dataFromCookies,
          maxEntertainmentSlotsLessThan7Nights,
          maxEntertainmentSlotsMoreThanOrEqual7Nights,
        });
      }
    }
  }, [
    privateRoute,
    history,
    setDataFromCookie,
    maxEntertainmentSlotsLessThan7Nights,
    maxEntertainmentSlotsMoreThanOrEqual7Nights,
  ]);

  const renderAlert = () => {
    if (bookingErrorMessage) {
      return <Alert message={bookingErrorMessage} clearErrorMessage={clearBookingSearchErrorMessage} />;
    }
    if (availableAddonsErrorMessage) {
      return <Alert message={availableAddonsErrorMessage} clearErrorMessage={clearAvailableAddonsErrorMessage} />;
    }
    if (saveAddonErrorMessage) {
      return <Alert message={saveAddonErrorMessage} clearErrorMessage={clearSaveAddonErrorMessage} />;
    }
    if (sessionCreationErrorMessage) {
      return <Alert message={sessionCreationErrorMessage} clearErrorMessage={clearCreateSessionErrorMessage} />;
    }
    if (nothingToBookErrorMessage) {
      return <Alert message={nothingToBookErrorMessage} />;
    }
    if (notFoundErrorMessage) {
      return <Alert message={notFoundErrorMessage} notFound />;
    }
    if (availableEntertainmentsErrorMessage) {
      return <Alert message={availableEntertainmentsErrorMessage} clearErrorMessage={clearAvailableEntertainmentsErrorMessage} />;
    }
    if (arrivalTimeErrorMessage) {
      return <Alert message={arrivalTimeErrorMessage} clearErrorMessage={clearArrivalTimeErrorMessage} />;
    }
    return null;
  };

  return (
    <>
      {isFetching && <Loading />}
      {!isFetching && renderAlert()}
      {
        !isFetching && showAppLinks && (
          <AppLinksOverlay
            playStoreLink={playStoreLink}
            appStoreLink={appStoreLink}
            resortMessage={resortMessage}
            positiveButtonText="OK"
            setShowAppModal={setShowAppModal}
          />
        )
      }
      {children}
    </>
  );
};

CustomRoute.propTypes = {
  children: PropTypes.node,
  privateRoute: PropTypes.bool,
  createSession: PropTypes.func.isRequired,
  setDataFromCookie: PropTypes.func,
  bookingErrorMessage: PropTypes.string.isRequired,
  availableAddonsErrorMessage: PropTypes.string.isRequired,
  saveAddonErrorMessage: PropTypes.string.isRequired,
  sessionCreationErrorMessage: PropTypes.string.isRequired,
  clearBookingSearchErrorMessage: PropTypes.func.isRequired,
  clearAvailableAddonsErrorMessage: PropTypes.func.isRequired,
  clearSaveAddonErrorMessage: PropTypes.func.isRequired,
  clearCreateSessionErrorMessage: PropTypes.func.isRequired,
  clearAvailableEntertainmentsErrorMessage: PropTypes.func.isRequired,
  isFetching: PropTypes.bool.isRequired,
  nothingToBookErrorMessage: PropTypes.string.isRequired,
  availableEntertainmentsErrorMessage: PropTypes.string.isRequired,
  notFoundErrorMessage: PropTypes.string,
  getConfig: PropTypes.func,
  maxEntertainmentSlotsLessThan7Nights: PropTypes.number,
  maxEntertainmentSlotsMoreThanOrEqual7Nights: PropTypes.number,
  bookingPrefix: PropTypes.string,
  clearArrivalTimeErrorMessage: PropTypes.func,
  arrivalTimeErrorMessage: PropTypes.string,
  showAppLinks: PropTypes.bool,
  playStoreLink: PropTypes.string,
  appStoreLink: PropTypes.string,
  resortMessage: PropTypes.string,
  isReloadNecessary: PropTypes.bool,
  setShowAppModal: PropTypes.func,
};

export default CustomRoute;
